/**
 * Sprint 006 (A1) — Shared page header.
 *
 * One heading style + count line + action slot for every list/detail
 * route. Replaces the per-route ad-hoc headers (which had drifted into
 * `text-3xl font-light` vs `text-2xl font-semibold` generations).
 *
 * Layout contract: AppShell already provides the outer container
 * (px + max-w). PageHeader only renders the header block — routes must
 * NOT wrap it in another `max-w-* mx-auto px-*` container.
 */
import { type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { PAGE_TITLE, TEXT } from './tokens';
import { useListContext } from '../../hooks/useListContext';

interface PageHeaderProps {
  title: ReactNode;
  /** Inline accessory next to the title (e.g. a legend trigger). */
  titleAccessory?: ReactNode;
  /** Count / context line under the title. */
  subtitle?: ReactNode;
  /** Right-aligned action buttons. */
  actions?: ReactNode;
  /** Optional controls row below the title row (view toggles etc.). */
  children?: ReactNode;
  /**
   * Sprint 006b (P2-Kanon) — Detail-/Form-Seiten: Chevron-Eyebrow über
   * dem Titel, zurück zur Eltern-Liste (Anatomie aus invoices_.$id
   * ratifiziert). Rein additiv; Listen-Seiten lassen es einfach weg.
   */
  backTo?: string;
  /** Label neben dem Chevron (z. B. "Rechnungen"). */
  backLabel?: ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  titleAccessory,
  subtitle,
  actions,
  children,
  backTo,
  backLabel,
  className = '',
}: PageHeaderProps) {
  const listSearch = useListContext(backTo);
  return (
    <div className={`mb-6 ${className}`}>
      <div className={`flex items-start justify-between gap-3 flex-wrap ${children ? 'mb-4' : ''}`}>
        <div className="min-w-0">
          {backTo && (
            <div className={`flex items-center gap-2 mb-1 ${TEXT.muted}`}>
              <Link
                to={backTo}
                search={listSearch}
                className="inline-flex items-center gap-2 min-h-11 hover:text-white/60 transition-colors"
                aria-label={typeof backLabel === 'string' ? `Zurück zu ${backLabel}` : 'Zurück'}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                {backLabel && <span>{backLabel}</span>}
              </Link>
            </div>
          )}
          <h1 className={`${PAGE_TITLE} inline-flex items-center gap-2`}>
            {title}
            {titleAccessory}
          </h1>
          {subtitle !== undefined && <p className={`${TEXT.tertiary} mt-1 text-sm`}>{subtitle}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2 min-w-0">{actions}</div>}
      </div>
      {children && <div className="flex items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

export default PageHeader;
